module Visonum.BulkPingApp.EditPanel
  ( view
  ) where

import Visonum.BulkPingApp.Prelude

import Data.Either (isLeft)
import Data.Maybe (Maybe(..))
import Data.String as Str
import Halogen.HTML.Elements as H
import Halogen.HTML.Events as HE
import Halogen.HTML.Properties (InputType(..))
import Halogen.HTML.Properties as HP
import Visonum.BulkPingApp.Components.App.Types (Action(..), State, parseInputVals)
import Visonum.BulkPingApp.Imgs (imgCross)
import Visonum.BulkPingApp.Views.PrimaryButton as PrimaryButton
import Visonum.BulkPingApp.Views.TextInput as TextInput

view :: forall w. State -> AppHTML w Action
view state =
  let
    elRoot = styleNode H.div
      "EditPanel"
      [ inline
          { display: "grid"
          , width: "100%"
          , padding: "5px"
          , gridTemplateColumns: "1fr 1fr"
          , gridTemplateRows: "auto auto auto"
          , gridTemplateAreas: Str.joinWith " "
              [ "\"Header Header\""
              , "\"LeftPart RightPart\""
              , "\"BottomPart BottomPart\""
              ]
          , outline: if state.showNewJob then "2px solid black" else "none"
          }
      ]

    elHeader = styleNode H.div
      "Header"
      [ inline
          { display: "flex"
          , flexDirection: "row"
          , gap: "10px"
          , gridArea: "Header"
          , justifyContent: "end"
          , margin: "5px"
          }
      ]

    elBottomPart = styleNode H.div
      "BottomPart"
      [ inline
          { display: "flex"
          , flexDirection: "row"
          , width: "100%"
          , gridArea: "BottomPart"
          , justifyContent: "end"
          , padding: "5px"
          }
      ]

    elLeftPart = styleNode H.div
      "LeftPart"
      [ inline
          { display: "flex"
          , flexDirection: "column"
          , gap: "10px"
          , gridArea: "LeftPart"
          , margin: "5px"
          }
      ]

    elRightPart = styleNode H.div
      "RightPart"
      [ inline
          { display: "flex"
          , flexDirection: "column"
          , gridArea: "RightPart"
          , margin: "5px"
          }
      ]

    elCloseButton = styleLeaf H.img
      "DetailsPanelCloseButton"
      [ inline
          { width: "20px"
          , height: "20px"
          , cursor: "pointer"
          }
      ]

  in
    elRoot
      []
      ( [ elHeader []
            [ if state.showNewJob then
                elCloseButton
                  [ HP.src imgCross
                  , HE.onClick $ const $ SetShowNewJob false
                  ]
              else PrimaryButton.view
                { label: "Add new Job"
                , onClick: SetShowNewJob true
                , disabled: false
                }
            ]
        ] <>
          if not state.showNewJob then []
          else
            [ elLeftPart []
                [ TextInput.view
                    TextInput.defaultProps
                      { label = "Agent URL"
                      , value = state.newJob.agentUrl
                      , onChange = \str -> UpdateJobInput $ state.newJob { agentUrl = str }
                      , onEnter = Nothing
                      }
                , TextInput.view
                    TextInput.defaultProps
                      { label = "Ping Count per IP"
                      , value = state.newJob.pingCountPerIp
                      , onChange = \str -> UpdateJobInput $ state.newJob { pingCountPerIp = str }
                      , onEnter = Nothing
                      }
                , TextInput.view
                    TextInput.defaultProps
                      { label = "Interval Between IPs (ms)"
                      , value = state.newJob.intervalBetweenIpsMillis
                      , onChange = \str -> UpdateJobInput $ state.newJob { intervalBetweenIpsMillis = str }
                      , onEnter = Nothing
                      }
                , TextInput.view
                    TextInput.defaultProps
                      { label = "Interval Between Pings (ms)"
                      , value = state.newJob.intervalBetweenPingsMillis
                      , onChange = \str -> UpdateJobInput $ state.newJob { intervalBetweenPingsMillis = str }
                      , onEnter = Nothing
                      }
                , viewPingMethodInput state
                ]
            , elRightPart []
                [ TextInput.view
                    TextInput.defaultProps
                      { label = "Destination IPs"
                      , value = state.newJob.destinationIps
                      , onChange = \str -> UpdateJobInput $ state.newJob { destinationIps = str }
                      , onEnter = Nothing
                      , multiline = true
                      }

                ]
            , elBottomPart []
                ( let
                    result = parseInputVals state.newJob
                  in
                    [ PrimaryButton.view
                        { label: "Submit Job"
                        , onClick: SubmitNewJob
                        , disabled: isLeft result
                        }
                    ]
                )
            ]
      )

viewPingMethodInput :: forall w. State -> AppHTML w Action
viewPingMethodInput state =
  let
    elRoot = styleNode H.div
      "PingMethodInput"
      [ inline
          { display: "grid"
          , gridTemplateColumns: "1fr 1fr 2fr"
          , gridTemplateRows: "60px"
          , gridTemplateAreas: Str.joinWith " "
              [ "\"Icmp SynCheck SynPort\""
              ]
          , gap: "5px"
          , padding: "2px"
          , marginTop: "5px"
          }
      ]

    elIcmp = styleNode H.div
      "Icmp"
      [ inline
          { gridArea: "Icmp"
          , border: "1px solid #ccc"
          , borderRadius: "5px"
          , display: "flex"
          , alignItems: "start"
          , padding: "5px"
          }
      ]

    elSyn = styleNode H.div
      "Syn"
      [ inline
          { display: "grid"
          , gridTemplateColumns: "subgrid"
          , gridColumnStart: "SynCheck"
          , gridColumnEnd: "SynPort"
          , border: "1px solid #ccc"
          , borderRadius: "5px"
          , alignItems: "center"
          , padding: "5px"
          }
      ]

    elCheckWrapper = styleNode H.div
      "CheckWrapper"
      [ inline
          { display: "flex"
          , flexDirection: "column"
          , gap: "5px"
          , padding: "5px"
          }
      ]

    elSynCheck = styleNode H.div
      "SynCheck"
      [ inline
          { gridArea: "SynCheck"
          }
      ]

    elSynPort = styleNode H.div
      "SynPort"
      [ inline
          { gridArea: "SynPort"
          }
      ]

    elCheck = styleLeaf H.input
      "Check"
      [ inline
          { width: "20px"
          , height: "20px"
          , accentColor: "black"
          }
      ]

    elLabel = styleNode H.label
      "Label"
      [ inline
          { fontSize: "10px"
          , fontFamily: "Arial"
          , fontWeight: "bold"
          }
      ]

  in
    elRoot
      []
      [ elIcmp []
          [ elCheckWrapper []
              [ elLabel []
                  [ text "Use ICMP"
                  ]
              , elCheck
                  [ HP.type_ InputCheckbox
                  , HP.checked $ state.newJob.useIcmp
                  , HE.onInput $ const $ UpdateJobInput $ state.newJob { useIcmp = not state.newJob.useIcmp }
                  ]
              ]
          ]
      , elSyn []
          [ elSynCheck []
              [ elCheckWrapper []
                  [ elLabel []
                      [ text "Use TCP SYN"
                      ]
                  , elCheck
                      [ HP.type_ InputCheckbox
                      , HP.checked $ state.newJob.useSyn
                      , HE.onInput $ const $ UpdateJobInput $ state.newJob { useSyn = not state.newJob.useSyn }
                      ]
                  ]
              ]
          , elSynPort []
              [ if state.newJob.useSyn then
                  TextInput.view
                    TextInput.defaultProps
                      { label = "Port"
                      , value = state.newJob.synPort
                      , onChange = \str -> UpdateJobInput $ state.newJob { synPort = str }
                      , onEnter = Nothing
                      }
                else text ""
              ]
          ]
      ]